import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import dailyService from '../../services/dailyService';
import { createLiveStream, updateStreamStatus, endLiveStream, LiveStream as LiveStreamType } from '../../services/videoStreamService';
import { DailyEventObject } from '@daily-co/daily-js';

interface LiveStreamProps {
  isOpen: boolean;
  onClose: () => void;
  onStreamCreated?: (stream: LiveStreamType) => void;
  title?: string;
  description?: string;
}

export const LiveStream: React.FC<LiveStreamProps> = ({
  isOpen,
  onClose,
  onStreamCreated,
  title,
  description,
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [stream, setStream] = useState<LiveStreamType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      initializeStream();
    } else {
      stopStream();
    }

    return () => {
      stopStream();
    };
  }, [isOpen]);

  const initializeStream = async () => {
    if (!user?.uid) {
      setError('Usuário não autenticado');
      return;
    }

    try {
      setIsStarting(true);
      setError(null);

      // Criar transmissão
      const newStream = await createLiveStream(
        user.uid,
        user.displayName || user.email || 'Usuário',
        title,
        description,
        true
      );

      setStream(newStream);
      if (onStreamCreated) {
        onStreamCreated(newStream);
      }

      // Inicializar Daily.co
      await dailyService.initialize();

      // Entrar na sala
      await dailyService.joinCall(newStream.roomUrl, user.displayName || 'Transmissor');

      // Configurar handlers
      dailyService.on('joined-meeting', () => {
        setIsStreaming(true);
        setIsStarting(false);
        updateStreamStatus(newStream.id, 'live');
        updateLocalVideo();
      });

      dailyService.on('left-meeting', () => {
        setIsStreaming(false);
      });

      dailyService.on('error', (event: DailyEventObject) => {
        console.error('Erro na transmissão:', event);
        setError('Erro na transmissão: ' + (event?.error?.message || 'Erro desconhecido'));
      });
    } catch (err: any) {
      console.error('Erro ao iniciar transmissão:', err);
      setError(err.message || 'Não foi possível iniciar a transmissão');
      setIsStarting(false);
    }
  };

  const updateLocalVideo = () => {
    if (localVideoRef.current) {
      const callObject = dailyService.getCallObject();
      if (callObject) {
        callObject.setLocalVideo(true);
        callObject.setLocalAudio(true);
        // Atualizar elemento de vídeo
        const videoTrack = callObject.participants().local?.videoTrack;
        if (videoTrack && localVideoRef.current) {
          localVideoRef.current.srcObject = new MediaStream([videoTrack]);
        }
      }
    }
  };

  const stopStream = async () => {
    try {
      if (stream) {
        await updateStreamStatus(stream.id, 'ended');
        await endLiveStream(stream.id);
      }
      await dailyService.leaveCall();
      await dailyService.destroy();
      setIsStreaming(false);
      setStream(null);
    } catch (err: any) {
      console.error('Erro ao parar transmissão:', err);
    }
  };

  const handleClose = async () => {
    await stopStream();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Transmissão Ao Vivo"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Encerrar Transmissão
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: theme.colors.errorContainer || '#fee2e2',
            color: theme.colors.onErrorContainer || '#991b1b',
            borderRadius: '8px',
            width: '100%',
            textAlign: 'center',
          }}>
            {error}
          </div>
        )}

        {isStarting && (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: theme.colors.textMuted,
          }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
            <div>Iniciando transmissão...</div>
          </div>
        )}

        {isStreaming && stream && (
          <>
            <div style={{
              width: '100%',
              maxWidth: '640px',
              borderRadius: '8px',
              overflow: 'hidden',
              backgroundColor: '#000',
              position: 'relative',
            }}>
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                style={{
                  width: '100%',
                  height: 'auto',
                }}
              />
              <div style={{
                position: 'absolute',
                top: '12px',
                left: '12px',
                padding: '6px 12px',
                backgroundColor: '#ef4444',
                color: 'white',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  animation: 'pulse 1s infinite',
                }} />
                AO VIVO
              </div>
            </div>

            {stream.roomUrl && (
              <div style={{
                padding: '12px',
                backgroundColor: theme.colors.surface,
                borderRadius: '8px',
                width: '100%',
                fontSize: '13px',
              }}>
                <div style={{ marginBottom: '8px', fontWeight: '600' }}>Link da Transmissão:</div>
                <div style={{
                  padding: '8px',
                  backgroundColor: theme.colors.surfaceVariant,
                  borderRadius: '4px',
                  wordBreak: 'break-all',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                }}>
                  {stream.roomUrl}
                </div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(stream.roomUrl);
                    alert('Link copiado!');
                  }}
                  style={{ marginTop: '8px' }}
                >
                  Copiar Link
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};




